import { useState, useRef } from "react";
import { FiX } from "react-icons/fi";
import Button from "../button";
import { registerUser, loginUser, verifyOtp } from "../../utils/Auth";
import { FaEye } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa";
import { useToast } from "../toast";
import { useNavigate } from "react-router-dom";
import { migrateGuestCartToUserCart } from "../../utils/handleGuestCart";
import { shopid, getUserId } from "../../utils/url";

const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("login"); // 'login' or 'signup'
  const [emailMobile, setEmailMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userActiveStatus, setUserActiveStatus] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [username, setUserName] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [noOfAttempts, setNoOfAttempts] = useState(1);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [errors, setErrors] = useState({});
  const { showToast, ToastContainer } = useToast();

  const inputRefs = useRef([]);
  if (!isOpen) return null;

  const handleRegisterUser = async () => {
    try {
      let newErrors = {};
      if (!email) newErrors.email = "Email is required";
      if (!mobile) newErrors.mobile = "Mobile number is required";
      if (!username) newErrors.username = "Username is required";
      else if (!/^[A-Za-z ]+$/.test(username))
        newErrors.username = "Username must contain only alphabets and spaces";

      if (!newPassword) newErrors.newPassword = "Password is required";
      if (!confirmPassword) newErrors.confirmPassword = "Confirm your Password";
      setErrors(newErrors);

      if (Object.keys(newErrors).length > 0) return;

      if (!email || !mobile || !username || !newPassword || !confirmPassword) {
        showToast({ message: "Please fill all the fields", type: "error" });
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast({ message: "Passwords do not match", type: "error" });
        return;
      }

      if (!termsChecked) {
        showToast({ message: "Please agree to the terms", type: "error" });
        return;
      }

      const statusCode = Number(
        await registerUser({
          email,
          mobile,
          userName: username,
          password,
        })
      );

      if (statusCode === 1) {
        showToast({ message: "Signup successful", type: "success" });
        setStep("login");
      } else if (statusCode === 2) {
        showToast({ message: "Email is already registered", type: "warning" });
      } else if (statusCode === 3) {
        showToast({
          message: "Username is already registered",
          type: "warning",
        });
      } else if (statusCode === 0) {
        showToast({
          message: "Mobile number is already registered",
          type: "warning",
        });
      }
    } catch (error) {
      showToast({ message: "Signup failed", type: "error" });
      console.log("Something went wrong", error);
    }
  };

  const handleGetOtp = async (mobile) => {
    try {
      setIsSendingOtp(true);
      if (mobile.trim().length < 10) {
        showToast({ message: "Enter a valid mobile number", type: "error" });
        return;
      }
      let loginStatusCode = await loginUser(mobile);
      setUserActiveStatus(loginStatusCode.userfound);
      if (loginStatusCode.otpsend == "Message Sent") {
        setNoOfAttempts((prev) => prev + 1);
        setStep("otp");
        if (noOfAttempts > 1) {
          showToast({ message: "OTP Re-sent Successfully", type: "success" });
          return;
        }
        showToast({ message: "OTP Sent Successfully", type: "success" });
      }
      else {
        showToast({ message: "Failed to send OTP. Please try again.", type: "failure" });
      }
    } catch (error) {
      showToast({
        message: "Failed to send OTP. Please try again.",
        type: "failure",
      });
      console.log("Something went wrong", error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const fullOtp = otp.join("");
    if (fullOtp.length !== 6) {
      showToast({
        message: "Please enter a valid 6 digit OTP",
        type: "warning",
      });
      return;
    }
    setIsVerifyingOtp(true); // start loader
    const result = await verifyOtp(emailMobile, fullOtp, userActiveStatus);
    if (result.success) {
      showToast({
        message: "OTP Verified Successfully",
        type: "success",
      });
      showToast({
        message: "User Logged in Successfully",
        type: "success",
      });

      // Get the userId from cookie (set by verifyOtp)
      const userId = getUserId();

      // Migrate guest cart to user cart before closing modal and reloading
      try {
        if (userId) {
          await migrateGuestCartToUserCart(userId, shopid);
        }
      } catch (error) {
        console.error("Error migrating guest cart:", error);
      }

      onClose();
      setStep("login");
      setEmailMobile("");
      setOtp(["", "", "", "", "", ""]);

      // Reload page after migration is complete
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      showToast({ message: "Invalid OTP", type: "failure" });
    }
    setIsVerifyingOtp(false); // stop loader
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-neutral-black bg-opacity-50 z-50">
      {/* SCROLLABLE, SAFE HEIGHT WRAPPER */}
      <div className="bg-white rounded-lg w-[90%] max-w-sm max-h-[90vh] overflow-y-auto p-6 relative no-scrollbar">
        {/* Close Button */}
        <button
          onClick={() => {
            onClose();
            setStep("login");
            setEmailMobile("");
            setPassword("");
            setNewPassword("");
            setEmail("");
            setMobile("");
            setUserName("");
            setConfirmPassword("");
            setTermsChecked(false);
          }}
          className="absolute top-3 right-3 text-2xl text-neutral-black mt-2 z-20"
        >
          <FiX />
        </button>

        {/* Spacing so content does not start behind the close button */}
        <div className="pt-6">
          {step === "login" && (
            <>
              <h2 className="text-xl font-semibold mb-4 text-neutral-black">
                LOGIN
              </h2>

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Mobile Number
              </label>
              <input
                type="text"
                placeholder="Enter Mobile Number"
                value={emailMobile}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setEmailMobile(numericValue);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleGetOtp(emailMobile);
                  }
                }}
                maxLength={10}
                className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:outline-none focus:border-gray-500 text-neutral-black"
              />

              <button
                onClick={() => handleGetOtp(emailMobile)}
                disabled={isSendingOtp}
                className={`w-full bg-primary-default font-semibold text-white py-2 rounded-md uppercase ${isSendingOtp && "opacity-50"
                  }`}
              >
                {isSendingOtp ? "Sending OTP..." : "Login"}
              </button>

            </>
          )}

          {step === "signup" && (
            <>
              <h2 className="text-xl font-semibold mb-4 text-neutral-black uppercase">
                Signup
              </h2>

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Email Id
              </label>
              <input
                type="text"
                placeholder="Enter Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((prev) => ({ ...prev, email: "" }));
                }}
                className={`w-full border rounded-md p-2 mb-1 focus:outline-none text-black ${errors.email ? "border-red-500" : "border-gray-300"
                  }`}
              />

              {errors.email && (
                <p className="text-red-500 text-xs mb-3">{errors.email}</p>
              )}

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Mobile Number
              </label>
              <input
                type="text"
                placeholder="Enter Mobile Number"
                value={mobile}
                maxLength={10}
                onChange={(e) => {
                  const numeric = e.target.value.replace(/\D/g, "");
                  setMobile(numeric);

                  if (numeric.length === 10) {
                    setErrors((prev) => ({ ...prev, mobile: "" }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      mobile: "Mobile number must be 10 digits",
                    }));
                  }
                }}
                className={`w-full border rounded-md p-2 mb-1 focus:outline-none text-black ${errors.mobile ? "border-red-500" : "border-gray-300"
                  }`}
              />

              {errors.mobile && (
                <p className="text-red-500 text-xs mb-3">{errors.mobile}</p>
              )}

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Username
              </label>
              <input
                type="text"
                placeholder="Enter Username"
                value={username}
                onChange={(e) => {
                  const onlyLettersAndSpaces = e.target.value.replace(
                    /[^A-Za-z ]/g,
                    ""
                  );
                  setUserName(onlyLettersAndSpaces);

                  if (onlyLettersAndSpaces.trim().length === 0) {
                    setErrors((prev) => ({
                      ...prev,
                      username:
                        "Username can contain only alphabets and spaces",
                    }));
                  } else {
                    setErrors((prev) => ({ ...prev, username: "" }));
                  }
                }}
                className={`w-full border rounded-md p-2 mb-1 focus:outline-none text-black ${errors.username ? "border-red-500" : "border-gray-300"
                  }`}
              />

              {errors.username && (
                <p className="text-red-500 text-xs mb-3">{errors.username}</p>
              )}

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Password
              </label>
              <div className="relative w-full mb-4">
                <input
                  type={showPassword ? "password" : "text"}
                  placeholder="Enter Password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, newPassword: "" }));
                  }}
                  className={`w-full border rounded-md p-2 mb-1 focus:outline-none text-black ${errors.newPassword ? "border-red-500" : "border-gray-300"
                    }`}
                />

                {errors.newPassword && (
                  <p className="text-red-500 text-xs mb-3">
                    {errors.newPassword}
                  </p>
                )}

                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Confirm Password
              </label>
              <div className="relative w-full mb-4">
                <input
                  type={showPassword ? "password" : "text"}
                  placeholder="Confirm your Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                  }}
                  className={`w-full border rounded-md p-2 mb-1 focus:outline-none text-black ${errors.confirmPassword
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                />

                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mb-3">
                    {errors.confirmPassword}
                  </p>
                )}

                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div className="mb-6 flex items-start mt-4">
                <input
                  type="checkbox"
                  id="termsAgreement"
                  className="mr-2 border-[#292929] mt-1"
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                />
                <label
                  htmlFor="termsAgreement"
                  className="text-black font-light"
                >
                  <span>I agree to the</span>
                  <span
                    onClick={() => {
                      onClose();
                      navigate("/coming-soon");
                    }}
                    className="text-[#5A189A] underline ml-1 cursor-pointer"
                  >
                    Terms of use & Privacy Policy
                  </span>
                </label>
              </div>

              <div className="flex justify-center">
                <button className="w-full bg-primary-default font-semibold text-white py-2 rounded-md uppercase" onClick={handleRegisterUser}>
                  Register
                </button>
              </div>

              <div className="text-neutral-black text-center mt-5">
                <p>
                  Already have an account ?{" "}
                  <span
                    className="uppercase underline text-primary-default cursor-pointer"
                    onClick={() => setStep("login")}
                  >
                    Login
                  </span>
                </p>
              </div>
            </>
          )}

          {step === "otp" && (
            <>
              <h2 className="text-xl text-black font-semibold mb-2">VERIFY WITH OTP</h2>
              <p className="text-sm mb-4 text-gray-700">
                Please enter the OTP sent to +91 xxxxxxxx
                {emailMobile.slice(-2)}
              </p>

              <label className="block mb-2 text-sm font-poppins font-normal text-custom-gray">
                Enter OTP
              </label>

              <div className="flex justify-between mb-3">
                {[1, 2, 3, 4, 5, 6].map((_, i) => (
                  <input
                    key={i}
                    type="tel"
                    inputMode="numeric"
                    maxLength="1"
                    ref={(el) => (inputRefs.current[i] = el)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      const newOtp = [...otp];
                      newOtp[i] = value;
                      setOtp(newOtp);
                      if (value && i < 5) inputRefs.current[i + 1].focus();
                    }}
                    onKeyDown={(e) => {
                      // Backspace navigation
                      if (e.key === "Backspace" && !e.target.value && i > 0) {
                        inputRefs.current[i - 1].focus();
                        return;
                      }
                      if (e.key === "Enter") {
                        handleVerifyOtp();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const paste = e.clipboardData
                        .getData("text")
                        .replace(/\D/g, "");
                      if (!paste) return;

                      const digits = paste.slice(0, 6).split("");
                      const newOtp = [...otp];

                      digits.forEach((digit, idx) => {
                        if (i + idx < 6) {
                          newOtp[i + idx] = digit;
                        }
                      });

                      setOtp(newOtp);

                      // Move focus to last filled box
                      const lastIndex = Math.min(i + digits.length - 1, 5);
                      inputRefs.current[lastIndex]?.focus();
                    }}
                    className="w-10 h-10 border border-gray-300 rounded-md text-center text-lg focus:outline-none focus:border-gray-500 text-neutral-black"
                  />
                ))}
              </div>

              <button
                onClick={() => handleGetOtp(emailMobile)}
                className="text-primary-default text-sm mb-4 underline cursor-pointer"
              >
                RESEND OTP
              </button>

              <button
                className={`w-full bg-primary-default font-semibold text-white py-2 rounded-md ${isVerifyingOtp && "opacity-50"
                  }`}
                onClick={handleVerifyOtp}
                disabled={isVerifyingOtp}
              >
                {isVerifyingOtp ? "Verifying OTP..." : "VERIFY OTP"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
